// useAdminMap — admin_id → display_name, for rendering "who did it" actor
// ids (created_by / decided_by / resolved_by / requested_by) as names
// instead of raw ULIDs. Admins are few, so one fetch covers any page; the
// query is shared/deduped by key, so calling the hook per component is cheap.
//
// The directory needs ADMIN_VIEW, which the ungated self-service pages
// cannot assume — My balance exists precisely for a CREATOR who holds
// nothing else, and its Recipient column is always that same person. So
// the map is seeded from /me/permissions, which every admin can read
// about themselves (SM-350). Without that seed the one row on the page
// that is guaranteed to be about YOU is the one that renders as a ULID.
//
// ── display_name is not an identity (SM-358) ─────────────────────────
//
// Nothing makes display_name unique — only `email` is (backoffice
// migration 0012). SIT alone carries five separate admins all called
// "Bootstrap admin", because `seed-admin.sh` defaults DISPLAY_NAME and
// QA runs seeded extra logins with fresh emails. Rendering the bare
// name then turns distinct people into indistinguishable rows: the
// Default rates table showed two "Bootstrap admin" rows at 20% and no
// way to tell which record a Change would edit.
//
// So a name shared by more than one admin is qualified with the thing
// that IS unique. The collision is judged across the whole directory,
// not per list, so a person's label does not change depending on which
// page or filter they appear under.
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMyPerms } from "@/hooks/useMyPerms";
import { fetchAdmins, fetchMyPermissions, keys } from "@/lib/queries";
import { Perm } from "@/lib/perms";
import type { AdminEntity } from "@/lib/types";

export type AdminNameMap = Map<string, string>;

// The directory is fetched whole, because every caller wants a complete
// answer: a label map that misses an id renders a ULID, and SM-365's
// rates table would silently omit an admin whose only sin is a late
// created_at. `/admin/admins` caps a page at 200 (backoffice pins the
// limit), so more than that means paging, not a bigger limit.
const DIRECTORY_PAGE = 200;
// A guard, not a policy: `total` comes from the server, and a wrong one
// must not turn a page render into an unbounded fetch loop.
const DIRECTORY_MAX_PAGES = 25;

/**
 * Every admin the caller may list, in one array. `/admin/admins` never
 * returns DELETED accounts, so "in this list" already means "still a
 * real admin" — callers do not re-filter for it.
 */
export async function fetchAdminDirectory(): Promise<AdminEntity[]> {
  const out: AdminEntity[] = [];
  for (let p = 0; p < DIRECTORY_MAX_PAGES; p++) {
    const { items, page } = await fetchAdmins({
      limit: DIRECTORY_PAGE,
      offset: p * DIRECTORY_PAGE,
    });
    out.push(...items);
    // A short page or a satisfied total ends it. Both are checked: an
    // endpoint that under-reports `total` still terminates on the empty
    // page after the last one.
    if (items.length === 0 || out.length >= (page?.total ?? 0)) break;
  }
  return out;
}

/**
 * Build id → label from the directory, qualifying any name that more
 * than one admin answers to. Exported for tests; `useAdminMap` is the
 * hook every caller wants.
 */
export function buildAdminLabels(
  admins: { admin_id: string; display_name?: string; email?: string }[],
): AdminNameMap {
  // A blank display_name is as unusable as a duplicated one, so the base
  // falls through to the next most legible identifier rather than
  // seeding a "" collision group.
  const base = new Map<string, string>();
  for (const a of admins) {
    if (!a.admin_id || base.has(a.admin_id)) continue;
    base.set(a.admin_id, a.display_name?.trim() || a.email?.trim() || a.admin_id);
  }

  const shared = new Set<string>();
  const seen = new Set<string>();
  for (const name of base.values()) {
    if (seen.has(name)) shared.add(name);
    seen.add(name);
  }

  const byID = new Map(admins.map((a) => [a.admin_id, a] as const));
  const out: AdminNameMap = new Map();
  for (const [id, name] of base) {
    if (!shared.has(name)) {
      out.set(id, name);
      continue;
    }
    // Email is the unique column; the id is the last resort for a
    // response that omitted it.
    const qualifier = byID.get(id)?.email?.trim() || id;
    out.set(id, qualifier === name ? name : `${name} (${qualifier})`);
  }
  return out;
}

export interface AdminDirectory {
  /** Every listable admin. Empty when the viewer may not list them. */
  admins: AdminEntity[];
  /** id → label, collision-qualified. Also seeded with the viewer. */
  labels: AdminNameMap;
  /**
   * False when the directory could not be read — a viewer without
   * ADMIN_VIEW gets a 403, which is an answer, not a fault. A caller
   * that enumerates people (rather than just naming ids) has to say so
   * instead of rendering an empty roster as "there is nobody".
   */
  available: boolean;
  loading: boolean;
}

/**
 * The admin roster plus its labels. One shared query, so a page that
 * already renders actor names pays nothing extra for the full records.
 */
export function useAdminDirectory(): AdminDirectory {
  const perms = useMyPerms();
  // Asked for only when the viewer may have it. `available: false` was
  // always the right answer for an admin without ADMIN_VIEW, but it was
  // reached by SENDING the request and reading the 403 back — one
  // guaranteed console error per page for any FINANCE reviewer, CREATOR
  // or READONLY, on a dozen screens that merely want to name an actor.
  // /me/permissions already says so before the fetch, so ask it first.
  //
  // Not a security boundary: `/admin/admins` is gated server-side and
  // stays that way. This only stops the client asking a question it has
  // already been told the answer to.
  const canList = perms.has(Perm.AdminView);
  const q = useQuery({
    queryKey: [...keys.admins, "name-map"] as const,
    queryFn: fetchAdminDirectory,
    staleTime: 60_000,
    // Still retry: false. The gate above spares the expected 403; a 403
    // that arrives anyway (a permission revoked mid-session, a role the
    // server scopes more tightly than the code implies) is an answer,
    // not a transient fault — the self seed below is what that viewer
    // gets instead.
    retry: false,
    enabled: canList,
  });
  // Same key as useMyPerms, so this costs nothing on a page that already
  // asked for permissions.
  const self = useQuery({
    queryKey: keys.myPerms(),
    queryFn: fetchMyPermissions,
    staleTime: 60_000,
    retry: 1,
  });
  // Memoized on the fetched data: callers feed this into further useMemo
  // chains, and fresh objects on every render would invalidate all of
  // them for no reason.
  return useMemo(() => {
    const admins = q.data ?? [];
    const labels = buildAdminLabels(admins);
    // Seeded, not overriding: the directory is authoritative when the
    // viewer can read it.
    const me = self.data;
    if (me?.admin_id && !labels.has(me.admin_id)) {
      labels.set(me.admin_id, me.display_name || me.email || me.admin_id);
    }
    return {
      admins,
      labels,
      available: !q.isError && q.data !== undefined,
      // Permissions in flight count as loading: until they land the
      // directory query has not been allowed to start, and a caller that
      // reads `available: false` in that gap paints "the roster is
      // unavailable" a frame before it becomes available. `self` settles
      // on error as well as success, so this cannot stick.
      loading: q.isLoading || self.isLoading,
    };
  }, [q.data, q.isError, q.isLoading, self.data, self.isLoading]);
}

export function useAdminMap(): AdminNameMap {
  return useAdminDirectory().labels;
}

/**
 * The same admins as a list, ordered by label — for pickers that let an
 * operator filter by "who" without knowing anyone's ULID. Two admins
 * sharing a display name arrive qualified, so a picker never offers the
 * same wording twice for different people; ties break on id so the order
 * cannot shuffle between renders.
 */
export function useAdminOptions(): { id: string; name: string }[] {
  const map = useAdminMap();
  return useMemo(
    () =>
      [...map.entries()]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id)),
    [map],
  );
}

/**
 * Resolve an actor id to a display label. Falls back to the raw id when it
 * isn't a known admin (e.g. an end-user actor) and to an em dash when empty.
 */
export function adminName(map: AdminNameMap, id: string | undefined | null): string {
  if (!id) return "—";
  return map.get(id) || id;
}

/**
 * The ownership flavour of `adminName` (SM-361).
 *
 * Two things differ from the actor-column case:
 *
 *   * an em dash is wrong for a missing owner. Catalog rows authored
 *     before the acting admin was stamped carry no `created_by` at all,
 *     and market-service omits the field rather than sending "" — the
 *     record genuinely has no known owner, which is a fact worth
 *     printing, not an empty cell.
 *   * a `created_by` the directory can't name still resolves to the raw
 *     id. That happens to a deactivated admin and to any CREATOR who
 *     lacks ADMIN_VIEW (they can only see their own rows anyway, and
 *     useAdminMap seeds their own name from /me/permissions). Degrading
 *     to the id keeps two same-looking rows distinguishable, which is
 *     the entire point of showing the owner.
 */
export function ownerName(map: AdminNameMap, id: string | undefined | null): string {
  if (!id) return "Unknown owner";
  return map.get(id) || id;
}

/**
 * The PROSE flavour: a label only when the directory can actually name
 * the id, and `null` otherwise — so a sentence can drop its "by …"
 * clause instead of printing a ULID mid-sentence.
 *
 * Both other flavours degrade to the raw id on purpose: in a table
 * column the id is the only thing keeping two same-looking rows apart
 * (SM-358), and a column always has a cell to fill. A sentence has no
 * such obligation. "Last saved by adm-633ab1b5fd0eb7e1 · 12 Aug" is
 * strictly worse than "Last saved 12 Aug" for the reader who triggered
 * it — an admin without ADMIN_VIEW, who by definition cannot look that
 * id up anywhere in this app.
 *
 * Note the viewer themselves always resolves: useAdminMap seeds their
 * own name from /me/permissions, so "you saved this" never degrades.
 */
export function namedActor(
  map: AdminNameMap,
  id: string | undefined | null,
): string | null {
  if (!id) return null;
  return map.get(id) ?? null;
}
